import {
  Maximize2,
  Camera,
  Crosshair,
  Eye,
  EyeOff,
  Focus,
  Frame,
  ListTree,
  RotateCcw,
  Ruler,
  Scissors,
  Square,
  Layers,
  Trash2,
  Undo2,
  ArchiveRestore,
  Box,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useViewer } from '@/features/viewer/ViewerContext';
import { useViewerStore } from '@/state/viewerStore';
import { cn } from '@/lib/cn';

export function Toolbar() {
  const {
    fitView,
    topView,
    resetView,
    hideSelected,
    isolateSelected,
    showAll,
    screenshot,
    zoomToSelection,
    toggleEdges,
    toggleMeasureMode,
    toggleSection,
    deleteSelected,
    undoDelete,
    restoreAllDeleted,
    toggleProjection,
  } = useViewer();
  const modelName = useViewerStore((s) => s.modelName);
  const selectedID = useViewerStore((s) => s.selection.expressID);
  const stats = useViewerStore((s) => s.stats);
  const treeOpen = useViewerStore((s) => s.treeOpen);
  const setTreeOpen = useViewerStore((s) => s.setTreeOpen);
  const edgesEnabled = useViewerStore((s) => s.edgesEnabled);
  const measureMode = useViewerStore((s) => s.measureMode);
  const sectionOpen = useViewerStore((s) => s.sectionOpen);
  const deletedIDs = useViewerStore((s) => s.deletedIDs);
  const projection = useViewerStore((s) => s.projection);

  const hasModel = modelName !== null;
  const hasSelection = selectedID !== null;
  const someHidden = stats.totalMeshes > 0 && stats.visibleMeshes < stats.totalMeshes;
  const deletedCount = deletedIDs.size;
  const isOrthographic = projection === 'orthographic';
  const treePanelVisible = treeOpen && hasModel;

  return (
    <div
      className={cn(
        'pointer-events-auto absolute top-1/2 z-20 flex -translate-y-1/2 flex-col gap-1 rounded-lg border border-panel-border bg-panel/85 p-1 backdrop-blur transition-[left]',
        treePanelVisible ? 'left-[19.5rem]' : 'left-3',
      )}
    >
      <ToolGroup>
        <ToolButton
          icon={<ListTree className="h-4 w-4" />}
          label={treeOpen ? 'Hide model tree' : 'Show model tree'}
          shortcut="B"
          onClick={() => setTreeOpen(!treeOpen)}
          disabled={!hasModel}
          highlighted={treePanelVisible}
        />
        <ToolButton
          icon={<Frame className="h-4 w-4" />}
          label={edgesEnabled ? 'Hide edges' : 'Show edges'}
          shortcut="E"
          onClick={toggleEdges}
          disabled={!hasModel}
          highlighted={edgesEnabled}
        />
        <ToolButton
          icon={<Ruler className="h-4 w-4" />}
          label={measureMode ? 'Exit measuring' : 'Measure distance'}
          shortcut="M"
          onClick={toggleMeasureMode}
          disabled={!hasModel}
          highlighted={measureMode}
        />
        <ToolButton
          icon={<Scissors className="h-4 w-4" />}
          label={sectionOpen ? 'Remove section' : 'Section plane'}
          shortcut="X"
          onClick={toggleSection}
          disabled={!hasModel}
          highlighted={sectionOpen}
        />
      </ToolGroup>

      <Divider />

      <ToolGroup>
        <ToolButton
          icon={<Maximize2 className="h-4 w-4" />}
          label="Fit to view"
          onClick={fitView}
          disabled={!hasModel}
        />
        <ToolButton
          icon={<Crosshair className="h-4 w-4" />}
          label="Zoom to selection"
          shortcut="F"
          onClick={zoomToSelection}
          disabled={!hasSelection}
        />
        <ToolButton
          icon={<Square className="h-4 w-4" />}
          label="Top view"
          shortcut="T"
          onClick={topView}
          disabled={!hasModel}
        />
        <ToolButton
          icon={<RotateCcw className="h-4 w-4" />}
          label="Reset view"
          shortcut="R"
          onClick={resetView}
          disabled={!hasModel}
        />
        <ToolButton
          icon={<Box className="h-4 w-4" />}
          label={isOrthographic ? 'Perspective view' : 'Orthographic view'}
          shortcut="O"
          onClick={toggleProjection}
          disabled={!hasModel}
          highlighted={isOrthographic}
        />
      </ToolGroup>

      <Divider />

      <ToolGroup>
        <ToolButton
          icon={<EyeOff className="h-4 w-4" />}
          label="Hide selected"
          shortcut="H"
          onClick={hideSelected}
          disabled={!hasSelection}
        />
        <ToolButton
          icon={<Focus className="h-4 w-4" />}
          label="Isolate selected"
          shortcut="I"
          onClick={isolateSelected}
          disabled={!hasSelection}
        />
        <ToolButton
          icon={<Eye className="h-4 w-4" />}
          label={someHidden ? `Show all (${stats.totalMeshes - stats.visibleMeshes} hidden)` : 'Show all'}
          shortcut="Shift+H"
          onClick={showAll}
          disabled={!someHidden}
          highlighted={someHidden}
        />
      </ToolGroup>

      <Divider />

      <ToolGroup>
        <ToolButton
          icon={<Trash2 className="h-4 w-4" />}
          label="Delete selected"
          shortcut="Del"
          onClick={deleteSelected}
          disabled={!hasSelection}
        />
        <ToolButton
          icon={<Undo2 className="h-4 w-4" />}
          label="Undo delete"
          shortcut="Ctrl+Z"
          onClick={undoDelete}
          disabled={deletedCount === 0}
        />
        <ToolButton
          icon={<ArchiveRestore className="h-4 w-4" />}
          label={deletedCount > 0 ? `Restore deleted (${deletedCount})` : 'Restore deleted'}
          onClick={restoreAllDeleted}
          disabled={deletedCount === 0}
          highlighted={deletedCount > 0}
        />
      </ToolGroup>

      <Divider />

      <ToolGroup>
        <ToolButton
          icon={<Camera className="h-4 w-4" />}
          label="Screenshot (PNG)"
          shortcut="S"
          onClick={() => void screenshot()}
          disabled={!hasModel}
        />
        <ToolButton
          icon={<Layers className="h-4 w-4" />}
          label={hasModel ? `${stats.visibleMeshes}/${stats.totalMeshes} meshes visible` : 'No model loaded'}
          onClick={() => {}}
          disabled
          asInfo
        />
      </ToolGroup>
    </div>
  );
}

function ToolGroup({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-0.5">{children}</div>;
}

function Divider() {
  return <div className="my-0.5 h-px bg-panel-border" />;
}

function ToolButton({
  icon,
  label,
  shortcut,
  onClick,
  disabled,
  highlighted,
  asInfo,
}: {
  icon: ReactNode;
  label: string;
  shortcut?: string;
  onClick: () => void;
  disabled?: boolean;
  highlighted?: boolean;
  asInfo?: boolean;
}) {
  const tooltip = shortcut ? `${label} · ${shortcut}` : label;
  return (
    <button
      type="button"
      title={tooltip}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        disabled
          ? 'cursor-default text-muted/50'
          : highlighted
            ? 'bg-accent/20 text-accent hover:bg-accent/30'
            : 'text-foreground hover:bg-white/8',
        asInfo && 'cursor-default',
      )}
    >
      {icon}
    </button>
  );
}
